import type { EvalRunSummary, ScorePoint } from '@/api/generated/model'
import type { ScoreSeries } from '@/components/evals/ScoreHistoryChart'

const VARIANT_QUERY_PARAM = 'variant'

interface TaskVariantPathInput {
  groupSlug: string
  taskSlug: string
  variant: string
}

export interface EvalRunStats {
  score: number | null
  bestScore: number | null
  lastRunAt: string | null
}

function runDate(run: EvalRunSummary): string {
  return run.finished_at ?? run.created_at
}

function chronologicalRuns(runs: EvalRunSummary[]): EvalRunSummary[] {
  return [...runs].sort((left, right) => {
    const dateDifference = new Date(runDate(left)).getTime() - new Date(runDate(right)).getTime()
    if (dateDifference !== 0) return dateDifference
    return new Date(left.created_at).getTime() - new Date(right.created_at).getTime()
  })
}

export function taskVariantPath({ groupSlug, taskSlug, variant }: TaskVariantPathInput): string {
  const searchParams = new URLSearchParams()
  searchParams.set(VARIANT_QUERY_PARAM, variant)
  return `/evals/groups/${groupSlug}/tasks/${taskSlug}?${searchParams.toString()}`
}

export function groupVariantPath({ groupSlug, variant }: { groupSlug: string; variant: string }): string {
  const searchParams = new URLSearchParams()
  searchParams.set(VARIANT_QUERY_PARAM, variant)
  return `/evals/groups/${groupSlug}?${searchParams.toString()}`
}

export function selectedTaskVariant(searchParams: URLSearchParams): string | null {
  if (!searchParams.has(VARIANT_QUERY_PARAM)) return null
  return searchParams.get(VARIANT_QUERY_PARAM) ?? ''
}

export function runsForVariant(runs: EvalRunSummary[], variant: string | null): EvalRunSummary[] {
  if (variant === null) return runs
  return runs.filter((run) => run.variant === variant)
}

/**
 * Only the runs that count toward scores. Reference runs (random, a
 * baseline, SOTA) mark a level rather than an achievement, so they'd
 * flatten a chart's climb and inflate "best score" — the API applies the
 * same filter to its own rollups (`eval_stats.scored`). The runs table
 * still lists them.
 */
export function scoredRuns(runs: EvalRunSummary[]): EvalRunSummary[] {
  return runs.filter((run) => run.reference_label === '')
}

export function scoreHistoryByVariant(runs: EvalRunSummary[]): ScoreSeries[] {
  const byVariant = new Map<string, EvalRunSummary[]>()
  for (const run of runs) {
    const existing = byVariant.get(run.variant)
    if (existing) {
      existing.push(run)
    } else {
      byVariant.set(run.variant, [run])
    }
  }
  return [...byVariant.keys()].sort().map((variant) => {
    const variantRuns = byVariant.get(variant)
    if (!variantRuns) throw new Error(`variant ${variant} vanished from its own grouping`)
    return { variant, points: scoreHistoryForRuns(variantRuns) }
  })
}

export function scoreHistoryForRuns(runs: EvalRunSummary[]): ScorePoint[] {
  return chronologicalRuns(runs).map((run) => ({
    date: runDate(run),
    score: run.overall_score,
    model: run.model_slug,
  }))
}

export function summarizeRuns(runs: EvalRunSummary[]): EvalRunStats {
  const ordered = chronologicalRuns(runs)
  const latest = ordered.length > 0 ? ordered[ordered.length - 1] : undefined
  return {
    score: latest?.overall_score ?? null,
    bestScore: runs.length > 0 ? Math.max(...runs.map((run) => run.overall_score)) : null,
    lastRunAt: latest ? runDate(latest) : null,
  }
}
